import React, { useState, useCallback, useMemo } from 'react';
import { Form, Button, InputGroup, Alert, ListGroup } from 'react-bootstrap';
import { AsyncTypeahead } from 'react-bootstrap-typeahead';
import { useTranslation } from 'react-i18next';
import debounce from 'lodash/debounce';
import { CommissionShare, FrontlineUser } from '@models/Commission';
import { FrontlineUserApi } from '@apis/FrontlineUserApi';

export type CommissionShareInputProps = {
  currentUser: { id: number; name: string };
  value: CommissionShare[];
  onChange: (shares: CommissionShare[]) => void;
  disabled?: boolean;
};

export const CommissionShareInput = ({ currentUser, value, onChange, disabled = false }: CommissionShareInputProps) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [options, setOptions] = useState<FrontlineUser[]>([]);

  const totalPercentage = useMemo(() => {
    return value.reduce((sum, share) => sum + share.commissionSharePercentage, 0);
  }, [value]);

  const isValid = totalPercentage === 100;

  const handleSearch = useCallback(
    debounce((query: string) => {
      void (async () => {
        setIsLoading(true);
        try {
          const users = await FrontlineUserApi.search({ search: query });
          const existingIds = value.map((s) => s.userId);
          setOptions(users.filter((u) => !existingIds.includes(u.id)));
        } catch (error) {
          setOptions([]);
        } finally {
          setIsLoading(false);
        }
      })();
    }, 300),
    [value],
  );

  const handleAddUser = (selected: FrontlineUser[]) => {
    if (selected.length === 0) return;

    const user = selected[0];
    const newShare: CommissionShare = {
      userId: user.id,
      userName: user.name || user.nameKm || user.mobileNumber,
      commissionSharePercentage: 0,
    };

    onChange([...value, newShare]);
    setOptions([]);
  };

  const handleRemoveUser = (userId: number) => {
    if (userId === currentUser.id) return;
    onChange(value.filter((s) => s.userId !== userId));
  };

  const handlePercentageChange = (userId: number, percentage: number) => {
    const updatedShares = value.map((share) =>
      share.userId === userId ? { ...share, commissionSharePercentage: Math.max(0, Math.min(100, percentage)) } : share,
    );
    onChange(updatedShares);
  };

  return (
    <div className="commission-share-input">
      <Form.Label>{t('commissionShare.title')}</Form.Label>

      {!disabled && (
        <AsyncTypeahead
          id="frontline-user-search"
          isLoading={isLoading}
          labelKey={(option) => {
            const user = option as FrontlineUser;
            return user.name || user.nameKm || user.mobileNumber;
          }}
          minLength={2}
          onSearch={handleSearch}
          options={options}
          onChange={(selected) => handleAddUser(selected as FrontlineUser[])}
          placeholder={t('commissionShare.searchPlaceholder')}
          renderMenuItemChildren={(option) => {
            const user = option as FrontlineUser;
            return (
              <div>
                <span>{user.name || user.nameKm}</span>
                {user.mobileNumber && <small className="text-muted ms-2">{user.mobileNumber}</small>}
              </div>
            );
          }}
          className="mb-3"
        />
      )}

      <ListGroup className="mb-3">
        {value.map((share) => (
          <ListGroup.Item key={share.userId} className="d-flex justify-content-between align-items-center">
            <div className="flex-grow-1">
              <span>{share.userName}</span>
              {share.userId === currentUser.id && (
                <small className="text-muted ms-2">({t('commissionShare.you')})</small>
              )}
            </div>
            <InputGroup style={{ width: '150px' }}>
              <Form.Control
                type="number"
                min={0}
                max={100}
                step={0.01}
                value={share.commissionSharePercentage}
                onChange={(e) => handlePercentageChange(share.userId, parseFloat(e.target.value) || 0)}
                disabled={disabled}
              />
              <InputGroup.Text>%</InputGroup.Text>
            </InputGroup>
            {share.userId !== currentUser.id && !disabled && (
              <Button
                variant="outline-danger"
                size="sm"
                className="ms-2"
                onClick={() => handleRemoveUser(share.userId)}
              >
                &times;
              </Button>
            )}
          </ListGroup.Item>
        ))}
      </ListGroup>

      <div className="d-flex justify-content-between align-items-center">
        <span className="fw-bold">{t('commissionShare.total')}</span>
        <span className={`fw-bold ${isValid ? 'text-success' : 'text-danger'}`}>{totalPercentage.toFixed(2)}%</span>
      </div>

      {!isValid && (
        <Alert variant="danger" className="mt-2 mb-0 py-2">
          {t('commissionShare.mustSum100')}
        </Alert>
      )}
    </div>
  );
};

export default CommissionShareInput;
